import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, BookOpenIcon, UsersIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline'
import { AcademicCapIcon, BriefcaseIcon } from '@heroicons/react/20/solid'

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function HowItWorksModal({ isOpen, onClose }: HowItWorksModalProps) {
  const learnerSteps = [
    {
      icon: BookOpenIcon,
      title: "Explore Fields",
      description: "Browse professionals across various industries and specialties"
    },
    {
      icon: UsersIcon,
      title: "Connect & Learn",
      description: "Book sessions with experts to gain insights and guidance"
    },
    {
      icon: AcademicCapIcon,
      title: "Accelerate Growth",
      description: "Fast-track your learning with personalized mentorship"
    }
  ]

  const professionalSteps = [
    {
      icon: BriefcaseIcon,
      title: "Share Expertise",
      description: "Create your profile showcasing your skills and experience"
    },
    {
      icon: UsersIcon,
      title: "Guide Others",
      description: "Mentor students and career changers in your field"
    },
    {
      icon: CurrencyDollarIcon,
      title: "Earn Income",
      description: "Get compensated for your valuable time and knowledge"
    }
  ]

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl max-h-[90vh] transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                  <Dialog.Title className="text-xl sm:text-2xl font-bold text-gray-900">
                    How Wonderstood Works
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Content - Scrollable */}
                <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-4 sm:p-8">
                  <div className="text-center mb-6 sm:mb-8">
                    <p className="text-base sm:text-lg text-gray-600">
                      Our platform connects two types of users to create meaningful learning experiences
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                    {/* For Learners */}
                    <div className="bg-blue-50 rounded-xl p-4 sm:p-6">
                      <div className="text-center mb-4 sm:mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mb-3 sm:mb-4">
                          <AcademicCapIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                          For Students & Career Explorers
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600">
                          New to a field, studying, or looking to make a career change
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {learnerSteps.map((step, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-blue-600 rounded-full">
                                <span className="text-sm font-semibold text-white">{index + 1}</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <step.icon className="w-4 h-4 text-blue-600" />
                                <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
                              </div>
                              <p className="text-sm text-gray-600">{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* For Professionals */}
                    <div className="bg-green-50 rounded-xl p-4 sm:p-6">
                      <div className="text-center mb-4 sm:mb-6">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-3 sm:mb-4">
                          <BriefcaseIcon className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                          For Industry Professionals
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Experienced professionals ready to share knowledge and earn income
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        {professionalSteps.map((step, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-8 h-8 bg-green-600 rounded-full">
                                <span className="text-sm font-semibold text-white">{index + 1}</span>
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <step.icon className="w-4 h-4 text-green-600" />
                                <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
                              </div>
                              <p className="text-sm text-gray-600">{step.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Section */}
                  <div className="mt-6 sm:mt-8 text-center pb-4">
                    <div className="bg-indigo-50 rounded-xl p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                        The Result: Mutual Success
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600">
                        Students and career explorers get the guidance they need to succeed, while professionals
                        build their network, share their passion, and earn compensation for their valuable expertise.
                      </p>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}